import { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Storage as StorageIcon } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useDataSources, useCreateDataSource, useDeleteDataSource } from '../api/dataSources';
import { useRealtimeKindsSettingIfAllowed, RealtimeKind } from '../api/realtimeKinds';
import { api } from '../utils/api';
import {
  DataSource,
  DataSourceFormData,
  INIT_TIME_SOURCE_TYPES,
  REALTIME_SOURCE_TYPES,
  SOURCE_TYPE_LABELS,
  SourceType,
  isRealtimeSourceType,
} from '../types/dataSource';
import DataSourceTable from '../components/dataManagement/DataSourceTable';
import DataSourceFormDialog from '../components/dataManagement/DataSourceFormDialog';
import FileManager from '../components/dataManagement/FileManager';

// One tab per init-time source type, then a single Realtime tab for every
// continuously polled kind — hotspots, lightning, emergency incidents, the
// organisation's own ignition-point and burnt-area layers, and whatever comes
// next. They are the same idea (a feed the poller watches) and splitting them
// across five tabs made a small list look like five empty ones.
type TabValue = SourceType | 'realtime' | 'file_manager';

const TAB_TYPES: { label: string; value: TabValue }[] = [
  ...INIT_TIME_SOURCE_TYPES.map((value) => ({ label: SOURCE_TYPE_LABELS[value], value })),
  { label: 'Realtime', value: 'realtime' },
  { label: 'File Manager', value: 'file_manager' },
];

// Order the realtime kinds are offered in when adding a source.
const REALTIME_KIND_ORDER: SourceType[] = [
  'hotspot',
  'lightning',
  'hazard',
  'ignition',
  'burnt_area',
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`data-tabpanel-${index}`}
      aria-labelledby={`data-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function DataManagementPage() {
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<DataSource | null>(null);

  const currentTabType = TAB_TYPES[tabValue].value;
  const isFileManagerTab = currentTabType === 'file_manager';
  const isRealtimeTab = currentTabType === 'realtime';
  // The realtime tab fetches everything and filters client-side: the list
  // endpoint takes one source_type, and a tenant's sources are few.
  const currentSourceType =
    isFileManagerTab || isRealtimeTab ? undefined : (currentTabType as SourceType);

  const queryClient = useQueryClient();
  const { dataSources, isLoading, error } = useDataSources(currentSourceType);
  const [saveError, setSaveError] = useState<string | null>(null);
  const visibleSources = isRealtimeTab
    ? dataSources.filter((s) => isRealtimeSourceType(s.source_type))
    : dataSources;

  // Realtime sources are manageable only by admins, and a metered kind only
  // once a superuser has enabled it for the tenant (the customer's own
  // ArcGIS kinds need no such switch and always report enabled). The setting
  // resolves to null for non-staff viewers (403 -> cannot manage).
  const { data: realtimeSetting } = useRealtimeKindsSettingIfAllowed();
  const canManageKind = (kind: SourceType): boolean =>
    !isRealtimeSourceType(kind) ||
    (realtimeSetting != null &&
      (realtimeSetting.can_edit ||
        (realtimeSetting.realtime_kinds?.[kind as RealtimeKind] ?? false)));
  const manageableRealtimeKinds = REALTIME_KIND_ORDER.filter(canManageKind);
  const canAddOnCurrentTab = isRealtimeTab ? manageableRealtimeKinds.length > 0 : true;
  const realtimeBlockedReason = !isRealtimeTab
    ? null
    : realtimeSetting == null
    ? 'Realtime data sources are managed by administrators.'
    : manageableRealtimeKinds.length === 0
    ? 'No realtime detection type is enabled for your organisation, so these data sources are read-only. Contact FiSci to have one enabled.'
    : null;
  const sourceTypeDisabledReason = (kind: SourceType): string | null =>
    canManageKind(kind) ? null : 'not enabled for your organisation';
  const createMutation = useCreateDataSource();
  const deleteMutation = useDeleteDataSource();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAdd = () => {
    setEditingSource(null);
    setDialogOpen(true);
  };

  const handleEdit = (source: DataSource) => {
    setEditingSource(source);
    setDialogOpen(true);
  };

  const handleDelete = async (source: DataSource) => {
    if (window.confirm(`Delete data source "${source.name}"?`)) {
      try {
        await deleteMutation.mutateAsync(source.id);
      } catch (err) {
        console.error('Error deleting data source:', err);
      }
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingSource(null);
    setSaveError(null);
  };

  const handleDialogSubmit = async (data: DataSourceFormData) => {
    try {
      if (editingSource) {
        await api.put(`/api/data_sources/${editingSource.id}`, data);
        queryClient.invalidateQueries({ queryKey: ['dataSources'] });
      } else {
        await createMutation.mutateAsync(data);
      }
      handleDialogClose();
    } catch (err) {
      console.error('Error saving data source:', err);
      // The server rejects some saves for reasons the user can act on — e.g.
      // a realtime kind that is not enabled for the organisation. Silently
      // swallowing it left the dialog looking like the click did nothing.
      const message =
        (err as { message?: string })?.message ??
        'Could not save the data source';
      setSaveError(
        message === 'Request failed' ? 'Could not save the data source' : message
      );
    }
  };

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Failed to load data sources.</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'var(--vh100)', bgcolor: '#f5f5f5' }}>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            bgcolor: 'primary.main',
            color: 'white',
            p: 2,
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <StorageIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Data Management
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          {!isFileManagerTab && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleAdd}
              disabled={!canAddOnCurrentTab}
            >
              + Add Data Source
            </Button>
          )}
        </Box>

        <Paper sx={{ width: '100%', mb: 2, boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="secondary"
            textColor="secondary"
            sx={{
              bgcolor: 'primary.light',
              '& .MuiTab-root': {
                color: 'white',
                '&.Mui-selected': { color: 'white', fontWeight: 'bold' },
                '&:focus': { outline: 'none' },
              },
            }}
          >
            {TAB_TYPES.map((tt) => (
              <Tab key={tt.value} label={tt.label} />
            ))}
          </Tabs>

          {isFileManagerTab ? (
            <TabPanel value={tabValue} index={tabValue}>
              <FileManager />
            </TabPanel>
          ) : isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TabPanel value={tabValue} index={tabValue}>
              {realtimeBlockedReason && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {realtimeBlockedReason}
                </Alert>
              )}
              <DataSourceTable
                sources={visibleSources}
                onEdit={handleEdit}
                onDelete={handleDelete}
                showType={isRealtimeTab}
                canManage={(source) => canManageKind(source.source_type)}
              />
            </TabPanel>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={!!saveError}
        autoHideDuration={8000}
        onClose={() => setSaveError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      </Snackbar>

      <DataSourceFormDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
        editingSource={editingSource}
        // On the realtime tab, start on the first kind this admin can add;
        // the dialog offers the rest of the family, greying out the
        // un-entitled ones.
        defaultSourceType={
          isRealtimeTab
            ? manageableRealtimeKinds[0] ?? 'ignition'
            : (currentSourceType ?? 'fire_history')
        }
        allowedSourceTypes={isRealtimeTab ? REALTIME_SOURCE_TYPES : INIT_TIME_SOURCE_TYPES}
        sourceTypeDisabledReason={isRealtimeTab ? sourceTypeDisabledReason : undefined}
        isSubmitting={createMutation.isPending}
      />
    </Box>
  );
}
